import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { api } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, KeyRound, Eye } from "lucide-react";

// Kelola akun Master Viewer: hanya bisa melihat overview semua area, tidak bisa ubah/tambah area
export default function MasterAdminDialog({ open, onOpenChange }) {
  const [list, setList] = useState(null);
  const [f, setF] = useState({ nomor_pegawai: "", nama: "", password: "" });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const load = () => api.get("/master/admins").then((r) => setList(r.data)).catch(() => setList([]));
  useEffect(() => { if (open) { setErr(""); setF({ nomor_pegawai: "", nama: "", password: "" }); load(); } }, [open]);

  const tambah = async (e) => {
    e.preventDefault(); setSaving(true); setErr("");
    try { await api.post("/master/admins", f); toast.success(`Akun ${f.nomor_pegawai.toUpperCase()} dibuat`); setF({ nomor_pegawai: "", nama: "", password: "" }); load(); }
    catch (ex) { setErr(ex.response?.data?.detail || "Gagal membuat akun"); } finally { setSaving(false); }
  };
  const reset = async (u) => {
    const pw = window.prompt(`Kata sandi baru untuk ${u.nomor_pegawai} (min. 6 karakter):`);
    if (!pw) return;
    try { const r = await api.post(`/master/admins/${u.nomor_pegawai}/reset`, { password: pw }); toast.success(r.data.message); }
    catch (ex) { toast.error(ex.response?.data?.detail || "Gagal reset kata sandi"); }
  };
  const hapus = async (u) => {
    if (!window.confirm(`Hapus akun ${u.nomor_pegawai}?`)) return;
    try { await api.delete(`/master/admins/${u.nomor_pegawai}`); toast.success("Akun dihapus"); load(); }
    catch (ex) { toast.error(ex.response?.data?.detail || "Gagal menghapus"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl" data-testid="master-admin-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Eye className="h-5 w-5 text-blue-600" />Kelola Admin Master (Viewer)</DialogTitle>
          <DialogDescription>Akun di sini hanya bisa <b>melihat overview keseluruhan lokasi</b> di halaman Master — tidak bisa menambah/mengubah area, mengelola admin, atau masuk ke dalam area.</DialogDescription>
        </DialogHeader>
        <form onSubmit={tambah} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_1fr_auto] gap-2 items-end rounded-lg border border-slate-200 bg-slate-50 p-3">
          <div className="space-y-1"><Label className="text-xs">No. Pegawai *</Label><Input value={f.nomor_pegawai} onChange={(e) => setF((s) => ({ ...s, nomor_pegawai: e.target.value.toUpperCase() }))} required className="h-9" data-testid="viewer-nomor" /></div>
          <div className="space-y-1"><Label className="text-xs">Nama</Label><Input value={f.nama} onChange={(e) => setF((s) => ({ ...s, nama: e.target.value }))} className="h-9" data-testid="viewer-nama" /></div>
          <div className="space-y-1"><Label className="text-xs">Kata Sandi *</Label><Input type="password" value={f.password} onChange={(e) => setF((s) => ({ ...s, password: e.target.value }))} required minLength={6} className="h-9" data-testid="viewer-password" /></div>
          <Button type="submit" disabled={saving} className="h-9 gap-1.5 bg-blue-600 hover:bg-blue-700 text-white" data-testid="viewer-add">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}Tambah</Button>
          {err && <p className="sm:col-span-4 text-sm text-rose-600" data-testid="viewer-error">{err}</p>}
        </form>
        <div className="max-h-72 overflow-y-auto rounded-lg border border-slate-200">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs text-slate-500"><tr><th className="px-3 py-2">No. Pegawai</th><th className="px-3 py-2">Nama</th><th className="px-3 py-2">Dibuat</th><th className="px-3 py-2 text-right">Aksi</th></tr></thead>
            <tbody data-testid="viewer-list">
              {list === null ? <tr><td colSpan={4} className="px-3 py-6 text-center"><Loader2 className="h-4 w-4 animate-spin inline" /></td></tr>
                : list.length === 0 ? <tr><td colSpan={4} className="px-3 py-6 text-center text-slate-400 text-xs">Belum ada akun viewer.</td></tr>
                : list.map((u) => (
                  <tr key={u.id} className="border-t border-slate-100" data-testid={`viewer-row-${u.nomor_pegawai}`}>
                    <td className="px-3 py-2 font-semibold text-slate-800">{u.nomor_pegawai}</td>
                    <td className="px-3 py-2">{u.name}</td>
                    <td className="px-3 py-2 text-xs text-slate-500">{String(u.created_at || "").slice(0, 10)}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <Button size="icon" variant="ghost" onClick={() => reset(u)} title="Reset kata sandi" className="h-8 w-8 text-blue-600" data-testid={`viewer-reset-${u.nomor_pegawai}`}><KeyRound className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => hapus(u)} title="Hapus" className="h-8 w-8 text-rose-600" data-testid={`viewer-delete-${u.nomor_pegawai}`}><Trash2 className="h-4 w-4" /></Button>
                    </td>
                  </tr>))}
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
}
